import { Component, Inject, OnInit, Pipe, PipeTransform } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { Roles } from '../../shared/enums/roles';
import { OrganizationsService } from '../organizations.service';
import { HomeService } from '../../home/home.service';
import { EntitlementTo } from '../../shared/models/entitlementTo';
import { SharedService } from '../../shared/shared.service';
import Swal from 'sweetalert2/dist/sweetalert2.js';

// Type for grouping entitlements by group name
type EntitlementGroupView = {
    groupName: string | null;
    entitlements: EntitlementTo[];
};

@Component({
    selector: 'app-org-entitlements',
    templateUrl: './org-entitlements.component.html',
    styleUrls: ['./org-entitlements.component.scss']
})
export class OrgEntitlementsComponent implements OnInit {
    // UI state and entitlement references
    selectAll: boolean = false;
    iotCreateEntitlement;
    iotSendLinkEntitlement;
    iotSendTextEntitlement;
    userRole = [];
    entitlementData: EntitlementTo[] = [];
    dirty = false; // Tracks if there are unsaved changes
    loading: boolean;
    roles = Roles;
    searchText: string;
    header = "Organization Entitlements";
    title;
    groupedEntitlements: { [group: string]: EntitlementTo[] } = {}; // Entitlements grouped by group name
    selectedEntitlementNames: Set<string> = new Set<string>(); // Currently selected entitlements
    disabledEntitlements: Set<string> = new Set<string>(); // Entitlements that cannot be selected
    groupVisibility: { [groupName: string]: boolean } = {}; // Controls group expand/collapse
    combinedGroups: EntitlementGroupView[] = []; // Used for rendering groups and ungrouped entitlements

    constructor(
        @Inject(MAT_DIALOG_DATA) public data,
        public dialogRef: MatDialogRef<OrgEntitlementsComponent>,
        public organizationsService: OrganizationsService,
        public homeService: HomeService,
        public sharedService: SharedService
    ) {}

    // On component initialization, set up entitlement references and load organization data
    ngOnInit() {
        this.iotCreateEntitlement = this.data.entitlements.find(e => e.name === "IOT_CREATE");
        this.iotSendLinkEntitlement = this.data.entitlements.find(e => e.name === "IOT_SEND_LINK");
        this.iotSendTextEntitlement = this.data.entitlements.find(e => e.name === "IOT_SEND_TEXT");
        this.getOrg();
    }

    // Loads organization data and entitlements, sets up selection state
    getOrg() {
        this.loading = true;
        this.organizationsService.getOrg(this.data.org.uuid).subscribe({
            next: data => {
                this.loading = false;
                this.data.org = JSON.parse(JSON.stringify(data));
                // Filter entitlements by org type
                this.entitlementData = this.data.entitlements?.filter(e => e.orgType === data.type);
                // Set selected entitlements based on org data
                if (this.data.org.entitlements) {
                    this.selectedEntitlementNames = new Set(
                        this.data.org.entitlements.filter(entitlementName =>
                            this.entitlementData.some(e => e.name === entitlementName)
                        )
                    );
                }
                
                if (this.data.org.disabledEntitlements) {
                    this.disabledEntitlements = new Set(
                        this.data.org.disabledEntitlements.filter(entitlementName =>
                            this.entitlementData.some(e => e.name === entitlementName)
                        )
                    );
                }
                
                this.groupEntitlements();
                this.updateCombinedGroups();
                this.title = this.data.org?.name;
                this.userRole = this.homeService.userDetails.roles.slice();
                this.setAllSelected();
            },
            error: error => {
                this.loading = false;
                this.dialogRef.close(false);
                this.sharedService.handleErrors(error);
            }
        });
    }

    // Groups entitlements by their group name for display
    groupEntitlements() {
        this.groupedEntitlements = {};
        this.groupVisibility = {};
        this.entitlementData.forEach(e => {
            const group = e.entitlementGroup;
            if (!group) return;
            if (!this.groupedEntitlements[group]) {
                this.groupedEntitlements[group] = [];
                this.groupVisibility[group] = false;
            }
            this.groupedEntitlements[group].push(e);
        });
    }

    // Returns entitlements that are not assigned to any group
    get unGroupedEntitlements(): EntitlementTo[] {
        return this.entitlementData.filter(e => !e.entitlementGroup || e.entitlementGroup.trim() === '');
    }

    // Updates the combinedGroups array for rendering
    updateCombinedGroups() {
        this.combinedGroups = this.generateCombinedGroups();
    }

    // Generates a sorted list of grouped and ungrouped entitlements for display
    generateCombinedGroups(): EntitlementGroupView[] {
        const result: EntitlementGroupView[] = [];
        for (const group in this.groupedEntitlements) {
            result.push({
                groupName: group,
                entitlements: this.groupedEntitlements[group].slice().sort((a, b) =>
                    (a.displayName || '').localeCompare(b.displayName || '')
                ),
            });
        }

        const unGrouped = this.unGroupedEntitlements.slice().sort((a, b) =>
            (a.displayName || '').localeCompare(b.displayName || '')
        );
        unGrouped.forEach(ent => {
            result.push({
                groupName: null,
                entitlements: [ent],
            });
        });

        return result.sort((a, b) => {
            const nameA = (a.groupName ? a.groupName : a.entitlements[0]?.displayName || '').toLowerCase();
            const nameB = (b.groupName ? b.groupName : b.entitlements[0]?.displayName || '').toLowerCase();
            return nameA.localeCompare(nameB);
        });
    }

    // Returns the display name for a group
    getGroupDisplayName(groupKey: string): string {
        return groupKey;
    }

    // Handles selection/deselection of an entitlement, including special logic for IOT entitlements
    changeEntitlements(value: boolean, entitlement: EntitlementTo) {
        this.dirty = true;

        if (value) {
            this.selectedEntitlementNames.add(entitlement.name);
        } else {
            this.selectedEntitlementNames.delete(entitlement.name);
        }

        // Special logic: if IOT_CREATE is deselected, also deselect dependent entitlements
        switch (entitlement.name) {
            case this.iotCreateEntitlement?.name:
                if (!value) {
                    this.selectedEntitlementNames.delete(this.iotSendLinkEntitlement.name);
                    this.selectedEntitlementNames.delete(this.iotSendTextEntitlement.name);
                }
                break;
            // If IOT_SEND_LINK or IOT_SEND_TEXT is selected, ensure IOT_CREATE is also selected
            case this.iotSendLinkEntitlement?.name:
            case this.iotSendTextEntitlement?.name:
                if (value) {
                    this.selectedEntitlementNames.add(this.iotCreateEntitlement.name);
                }
                break;
        }

        this.setAllSelected();
    }

    // Checks if an entitlement is selected
    isSelected(entitlementName: string): boolean {
        return this.selectedEntitlementNames.has(entitlementName) === true;
    }

    // Checks if an entitlement is selected
    isEnabled(entitlementName: string): boolean {
        return this.isSelected(entitlementName) && this.disabledEntitlements.has(entitlementName) === false;
    }

    // Checks if all entitlements in a group are selected
    isGroupFullySelected(group: string): boolean {
        return this.groupedEntitlements[group].every(e => this.selectedEntitlementNames.has(e.name));
    }

    // Checks if some (but not all) entitlements in a group are selected
    isGroupPartiallySelected(group: string): boolean {
        const selectedCount = this.groupedEntitlements[group].filter(e => this.selectedEntitlementNames.has(e.name)).length;
        return selectedCount > 0 && selectedCount < this.groupedEntitlements[group].length;
    }

    // Toggles visibility (expand/collapse) of a group
    toggleGroupVisibility(group: string): void {
        this.groupVisibility[group] = !this.groupVisibility[group];
    }

    // Selects or deselects all entitlements in a group
    toggleGroup(group: string, checked: boolean): void {
        const groupEntitlements = this.groupedEntitlements[group];
        if (!groupEntitlements) return;

        this.dirty = true;
        groupEntitlements.forEach(entitlement => {
            this.changeEntitlements(checked, entitlement);
        });
    }

    // Toggles selection of a single entitlement by name
    toggleSingleEntitlement(entitlement: EntitlementTo, checked: boolean) {
        if (checked) {
            if (entitlement.parentEntitlementId !== undefined) {
                const parentEntitlement = this.entitlementData.find(e => e.id === entitlement.parentEntitlementId);
                this.selectedEntitlementNames.add(parentEntitlement.name);
            }
            this.selectedEntitlementNames.add(entitlement.name);
        } else {
            const childrenEntitlements = this.entitlementData.filter(e => e.parentEntitlementId !== undefined && e.parentEntitlementId === entitlement.id)
            childrenEntitlements.forEach(childEntitlement => {
                this.selectedEntitlementNames.delete(childEntitlement.name);
            });
            this.selectedEntitlementNames.delete(entitlement.name);
        }
        this.dirty = true;
        this.setAllSelected();
    }

    toggleSingleEntitlementEnabledSlide(evnt: MatSlideToggleChange, entitlementName: string) {
        console.log('toggleSingleEntitlementEnabledSlide', evnt, entitlementName);
        if (evnt.checked) {
            this.disabledEntitlements.delete(entitlementName);
        } else {
            this.disabledEntitlements.add(entitlementName);
        }
        this.dirty = true;
        this.setAllSelected();
    }

    // Updates the selectAll flag based on current selection state
    setAllSelected() {
        this.selectAll = this.entitlementData.every(e => this.selectedEntitlementNames.has(e.name));
    }

    // Handles toggling selection of all entitlements
    selectAllToggle(value: boolean): void {
        this.dirty = true;
        if (value) {
            this.entitlementData.forEach(e => this.selectedEntitlementNames.add(e.name));
        } else {
            this.selectedEntitlementNames.clear();
        }
        this.setAllSelected();
    }

    removeUnselectedEntitlementsFromDisabledEntitlements() {
        // Remove any disabled entitlements that are not selected
        this.disabledEntitlements.forEach(entitlementName => {
            if (!this.selectedEntitlementNames.has(entitlementName)) {
                this.disabledEntitlements.delete(entitlementName);
            }
        });
    }
    // Saves the selected entitlements to the organization and updates user info
    updateOrgEntitlements() {
        this.data.org.entitlements = Array.from(this.selectedEntitlementNames);
        this.removeUnselectedEntitlementsFromDisabledEntitlements();
        this.data.org.disabledEntitlements = Array.from(this.disabledEntitlements);

        this.organizationsService.updateOrganization(this.data.org).subscribe({
            next: () => {
                this.sharedService.showAlert('success', 'Organization Entitlements updated successfully');
                this.homeService.updateUserInfo().subscribe();
                // Only close (and thus trigger the org list's afterClosed() reload) once the
                // save has actually succeeded — closing earlier raced the list refresh against
                // the still-in-flight save and could show stale entitlement state.
                this.dialogRef.close(true);
            },
            error: error => {
                this.sharedService.handleErrors(error, null, 'Unable to update organization entitlements');
            }
        });
    }

    // Resets entitlements to the last loaded state
    resetEntitlements() {
        this.doOnConfirm(() => this.getOrg(), 'Yes, Reset');
        this.searchText = "";
    }

    // Closes the dialog, confirming if there are unsaved changes
    close() {
        this.doOnConfirm(() => this.dialogRef.close(false), 'Yes, Close');
    }

    // Helper for confirmation dialogs before destructive actions
    private doOnConfirm(action, confirmText) {
        if (this.dirty) {
            Swal.fire({
                title: 'Are you sure?',
                text: 'Your changes will be lost!',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#DD6B55',
                confirmButtonText: confirmText,
                cancelButtonText: 'No, Cancel',
            }).then(result => {
                if (result.value) {
                    action.call();
                    this.dirty = false;
                }
            });
        } else {
            action.call();
        }
    }

    disableToggleSlide(entitlementName: string): boolean {
        // Disable toggle if user is not SUPER or ADMIN and entitlement is not selected
        let isGod = this.userRole.includes(this.roles.SUPER);
        let isAdmin = this.userRole.includes(this.roles.ADMIN);
        let isSelected = this.isSelected(entitlementName);
        if (isGod) return false;
        if (isAdmin && isSelected) return false;
        return true; // Disable toggle if not SUPER or ADMIN and entitlement is not selected
    }
}

// Pipe for filtering entitlements by search text
@Pipe({ name: 'search' })
export class SearchPipe implements PipeTransform {
    transform(items: any[], searchText: string): any[] {
        if (!searchText) return items;
        return items.filter(it => {
            return it.displayName.toLowerCase().includes(searchText.toLowerCase());
        });
    }
}
